import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import CategoriesNav from '@app/components/common/CategoriesNav';
import Espot from '@app/components/common/Espot/Espot';
import { useBreakpoint, useEspot } from '@app/hooks';
import { sortAlphabeticByAttribute } from '@app/utils';
import {
  CATEGORIES_PAGE_ESPOT_2,
  CATEGORIES_PAGE_ESPOT_3,
  CATEGORIES_PAGE_ESPOT_4,
  CATEGORIES_SIZE
} from '../constants';
import ShopAllCategoriesGrid from '../ShopAllCategoriesGrid';
import Page from '@app/components/common/Page';
import startCase from 'lodash/startCase';
import toLower from 'lodash/toLower';
import { PAGE_BREAKPOINTS, STATUS } from '@app/constants/commonConstants';

const ShopAllCategoriesPage = () => {
  const [t] = useTranslation();
  const isLG = useBreakpoint(PAGE_BREAKPOINTS.LG);
  const { getEspotContent, isEspotLoading } = useEspot(
    [
      CATEGORIES_PAGE_ESPOT_2,
      CATEGORIES_PAGE_ESPOT_3,
      CATEGORIES_PAGE_ESPOT_4
    ].join()
  );

  const categories = useSelector(s => s.categories?.allCategories);
  const categoriesStatus = useSelector(s => s.categories?.categoriesStatus);

  const isCategoriesLoading =
    categoriesStatus === STATUS.IDLE || categoriesStatus === STATUS.PENDING;

  const seoURL = useSelector(s => s.dealer.seoURL);

  const breadCrumbsLinks = [
    { label: t('BCT_HOME'), value: seoURL || '/' },
    { label: startCase(toLower(t('SEARCH_ALL_DEPARTMENTS'))) }
  ];

  const categoriesList = useMemo(() => {
    if (categories) {
      return sortAlphabeticByAttribute(categories, 'name').map(item => ({
        ...item,
        redirectUrl: item.seoURL
      }));
    }
    return [];
  }, [categories]);

  return (
    <Page>
      <Page.BreadCrumb breadCrumbs={breadCrumbsLinks} showBackButton={false} />
      <Page.Header>
        <h1 className="h1 display-1 u-fs--40">{t('SHOP_BY_CATEGORY')}</h1>
      </Page.Header>
      <Page.Layout>
        <Page.Layout.SideBar>
          <CategoriesNav
            categories={categoriesList}
            isLoading={isCategoriesLoading}
            skeletonSize={CATEGORIES_SIZE}
          />
        </Page.Layout.SideBar>
        <Page.Layout.Content>
          <ShopAllCategoriesGrid
            categories={categoriesList}
            isLoading={isCategoriesLoading}
            espot2Html={getEspotContent(CATEGORIES_PAGE_ESPOT_2)}
            espot3Html={getEspotContent(CATEGORIES_PAGE_ESPOT_3)}
            isEspotLoading={isEspotLoading}
            skeletonSize={CATEGORIES_SIZE}
          />
        </Page.Layout.Content>
      </Page.Layout>
      <Espot
        isLoading={isEspotLoading}
        skeletonClassName={`height-${isLG ? '200' : '100'}px mb-3 mt-1`}
        html={getEspotContent(CATEGORIES_PAGE_ESPOT_4)}
      />
    </Page>
  );
};

export default ShopAllCategoriesPage;
